import moment from "moment";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  CandidateState,
  Currency,
  mtw_companies,
  PhoneCandidateState,
} from "./constant.ts";
import { useConfirm } from "../hooks/useConfirm.tsx";
import { handleBookCall } from "../api/ApiHelpers.ts";
import { InterviewType } from "../features/OldVersion/RoleDetails/constants.ts";

export const wait = (waitTimeInMs = 2000) =>
  new Promise((resolve) => setTimeout(resolve, waitTimeInMs));

export const isMTW = (slug) => mtw_companies.includes(slug);

export const capitalizeFirstLetter = (str: string) => {
  return str ? str?.charAt(0)?.toUpperCase() + str?.slice(1) : "";
};

export const getFullName = (
  firstName: string,
  lastName: string,
  callingName?: string
) => {
  return firstName || lastName
    ? capitalizeFirstLetter(firstName?.toLowerCase()) +
        " " +
        capitalizeFirstLetter(lastName?.toLowerCase())
    : capitalizeFirstLetter(callingName?.toLowerCase());
};

export const keyToTitle = (key: string) => {
  return key
    ? key
        .split("_")
        .map((word) => capitalizeFirstLetter(word))
        .join(" ")
    : "";
};

export const arrayToString = (arr: any[]) => {
  if (typeof arr == "string") {
    return arr;
  }
  if (arr?.length == 1) {
    return arr[0];
  }
  return arr?.slice(0, -1).join(", ") + ", and " + arr[arr?.length - 1];
};

export const validateEmail = (email) => {
  return !!String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )?.length;
};

export const validatePhoneNumber = (phone) => {
  return isValidPhoneNumber(phone);
};

export const formatDate = (date, format = "DD-MM-YYYY") =>
  date ? moment(date, format).format("DD MMM 'YY") : "";

export const formatDateTime = (date, format = "DD/MM/YYYY hh:mm A") =>
  date ? moment(date, format).format("DD MMM 'YY, hh:mm A") : "";

export const formatCurrency = (value: string, currency: Currency) => {
  const cleaned = value.toString().replace(/\D/g, "");
  if (!cleaned) return "";

  if (currency === Currency.USD)
    return (
      Currency.USD +
      Number(cleaned).toLocaleString("en-US", {
        maximumFractionDigits: 0,
      })
    );
  if (currency === Currency.INR)
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(+cleaned);
};

export const toDisplayLacs = (
  amount: number | null | undefined
): number | null => {
  if (!amount) return null;
  return amount > 100 ? +(amount / 100000).toFixed(2) : amount;
};

export const textAreaAdjust = (inputRef) => {
  inputRef.current.style.height = "auto";
  inputRef.current.style.height = inputRef?.current.scrollHeight + "px";
};

export const useUpgrade = () => {
  const confirm = useConfirm();

  const upgrade = async () => {
    const ok = await confirm({
      title: "Upgrade!",
      submitText: "Book a meeting to upgrade",
      hideCancel: true,
      submitBtnType: "save",
      content:
        "Hey! This feature is currently locked. To unlock features like email invitation, AI interviews and many more, upgrade to premium.",
    });
    if (ok) {
      handleBookCall();
    }
  };

  return upgrade;
};

export const uniqueElements = (array) => [...new Set(array)];

export const formatNumber = (num: number | string) => {
  return Number(num || 0).toLocaleString("en-IN");
};

export const formatIfCaseString = (str) => {
  if (typeof str !== "string") return str;

  // detect snake_case OR kebab-case
  const isSnakeOrKebab = str.includes("_") || str.includes("-");

  if (!isSnakeOrKebab) return str;

  return str
    .replace(/[-_]/g, " ") // replace - and _ with space
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const isSlug = () => {
  const firstParam = window.location.pathname.split("/")[1] || "";
  const routes = ["onboard-form", "control"];
  if (firstParam && routes.includes(firstParam)) return false;
  return true;
};

export const getStatusFromState = (type, status) => {
  const states =
    type === InterviewType.Phone ? PhoneCandidateState : CandidateState;
  return Object.entries(states).find(([key, value]) =>
    value?.split(",").includes(status)
  )?.[0];
};

export const formatDuration = (timeInSecs) => {
  const hours = Math.floor(timeInSecs / 3600);
  const minutes = Math.floor((timeInSecs % 3600) / 60);
  const seconds = Math.floor(timeInSecs % 60);

  return `${hours > 0 ? String(hours).padStart(2, "0") + "h " : ""}${minutes}m  ${seconds}s`;
};

export const isSafari = () => {
  const ua = navigator.userAgent;
  // Safari contains Safari but excludes Chrome/Edge/Android
  return (
    /Safari/i.test(ua) &&
    !/Chrome/i.test(ua) &&
    !/CriOS/i.test(ua) &&
    !/Edg/i.test(ua) &&
    !/Android/i.test(ua)
  );
};
